/**
 * Multi-domain runtime resolution — o MESMO bundle do Cloudflare Pages é servido
 * em vários hosts, então resolve o BFF + SSO pelo domínio SERVIDO em runtime (não
 * confia no VITE_* baked; espelha o recruiter/candidate SPA):
 *   - `*.eureca.me`                  → prd (sso.eureca.me / admin-api.eureca.me)
 *   - `*.eureca.dev` (NÃO `*.stg.…`) → .dev (sso.eureca.dev / admin-api.eureca.dev)
 *   - localhost / `*.stg.eureca.dev` → VITE_* baked (dev/staging)
 */
function onEurecaMe(): boolean {
  if (typeof window === 'undefined') return false;
  const h = window.location.hostname;
  return h === 'eureca.me' || h.endsWith('.eureca.me');
}

/**
 * Slots de QA (spec 086): hosts de um nível `qa<N>-<app>.eureca.dev` no mesmo
 * sufixo do canary. O slot-spas.sh embute o BFF (`qa<N>-admin-api`) e o
 * Keycloak (`qa<N>-auth`) do próprio slot nos VITE_*, então o slot fica FORA do
 * `onEurecaDev` — senão o SPA do slot falaria com a stack canary. Mesma regra
 * (e testes) do candidate/recruiter; o admin não tem suíte vitest.
 */
function isSlotHostname(h: string): boolean {
  return /^qa[12]-[a-z-]+\.eureca\.dev$/.test(h);
}

/**
 * Efêmero por PR (spec 070): preview 1-nível `<app>-pr<N>.eureca.dev`, com o BFF
 * (`admin-api-pr<N>`) e o Keycloak (`auth-pr<N>`) do PR embutidos pelo
 * build-spas.sh. A distinção efêmero×canary é o LABEL `-pr<N>`, nunca
 * `.eureca.dev` cru (senão o SPA do PR falaria com a stack canary — caça BR-1,
 * spec 125). Vale enquanto o efêmero por PR existir, ao lado da regra dos slots.
 */
export function isEphemeralHostname(h: string): boolean {
  return /-pr\d+\.eureca\.dev$/.test(h);
}

// Slot de QA: silent-check same-origin (servido pelo nginx do slot).
function onSlot(): boolean {
  if (typeof window === 'undefined') return false;
  return isSlotHostname(window.location.hostname);
}

// Efêmero por PR: idem, servido pelo nginx do PR.
function onEphemeral(): boolean {
  if (typeof window === 'undefined') return false;
  return isEphemeralHostname(window.location.hostname);
}

function onEurecaDev(): boolean {
  if (typeof window === 'undefined') return false;
  const h = window.location.hostname;
  return (
    (h === 'eureca.dev' || h.endsWith('.eureca.dev')) &&
    !h.endsWith('.stg.eureca.dev') &&
    !onSlot() &&
    !onEphemeral()
  );
}

const API_URL = onEurecaMe()
  ? 'https://admin-api.eureca.me'
  : onEurecaDev()
    ? 'https://admin-api.eureca.dev'
    : import.meta.env.VITE_API_URL || 'http://localhost:3002';

const KEYCLOAK_URL = onEurecaMe()
  ? 'https://sso.eureca.me'
  : onEurecaDev()
    ? 'https://sso.eureca.dev'
    : (import.meta.env.VITE_KEYCLOAK_URL as string);

const SILENT_CHECK_SSO_REDIRECT_URI =
  onEurecaMe() || onEurecaDev() || onSlot() || onEphemeral()
    ? `${window.location.origin}/silent-check-sso.html`
    : (import.meta.env.VITE_KEYCLOAK_SILENT_CHECK_SSO_REDIRECT_URI as string | undefined) ||
      undefined;

export const adminEnv = {
  appName: import.meta.env.VITE_APP_NAME || 'admin',
  appDisplayName: import.meta.env.VITE_APP_DISPLAY_NAME || 'EURECA Admin',
  apiUrl: API_URL,
  keycloak: {
    url: KEYCLOAK_URL,
    realm: import.meta.env.VITE_KEYCLOAK_REALM as string,
    clientId: import.meta.env.VITE_KEYCLOAK_CLIENT_ID as string,
    // Admin é 100% autenticado (sem página pública) → login-required por padrão.
    onLoad:
      import.meta.env.VITE_KEYCLOAK_ON_LOAD === 'check-sso'
        ? ('check-sso' as const)
        : ('login-required' as const),
    silentCheckSsoRedirectUri: SILENT_CHECK_SSO_REDIRECT_URI,
  },
} as const;
